import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'
import { ClassName } from 'types/props'

export type OperationType = 'progress' | 'negative' | 'neutral' | 'cancel'

interface CoreButtonProps extends ClassName {
  height?: string
  id?: string
  isDisabled?: boolean
  isLoading?: boolean
  operationType: OperationType
  padding?: string
  width?: string
}

export interface OperationButtonProps extends CoreButtonProps {
  children: any
  isDisabled?: boolean
  isLoading?: boolean
  onClick?: (e: any) => void
  type?: 'button' | 'submit' | 'reset'
}

function getBackground(type: OperationType) {
  switch (type) {
    case 'progress':
      return 'var(--Orange-Dark)'
    case 'negative':
      return 'var(--Orange-Dark)'
    case 'neutral':
      return 'var(--White70)'
    case 'cancel':
      return 'var(--Red-Dark)'
  }
}

function getHoverBackground(type: OperationType) {
  switch (type) {
    case 'progress':
      return 'var(--Orange-Light)'
    case 'negative':
      return 'var(--Orange-Light)'
    case 'neutral':
      return 'var(--White80)'
    case 'cancel':
      return 'var(--Graphite)'
  }
}

function getForeground(type: OperationType) {
  switch (type) {
    default:
      return 'var(--White)'
  }
}

function getHoverForeground(type: OperationType) {
  switch (type) {
    default:
      return 'var(--White)'
  }
}

const Button = styled.button<CoreButtonProps>`
  min-width: ${props => (props.operationType !== 'neutral' ? '180px' : '')};
  ${props => (props.width ? `width: ${props.width}` : '')};
  ${props => (props.height ? `height: ${props.height}` : '')};
  padding: ${props => (props.padding ? props.padding : '10px 30px')};
  height: 40px;
  font-size: 15px;
  border-radius: 4px;
  border: none;
  display: flex;
  align-items: center;
  color: ${props => getForeground(props.operationType)};
  cursor: pointer;
  background: none;
  background-color: ${props => getBackground(props.operationType)};
  justify-content: center;
  box-shadow: 0 0 0 0 var(--White90);
  transition: ease-out 0.17s all;
  position: relative;
  overflow: hidden;
  pointer-events: ${props => (props.isLoading ? 'none' : 'all')};

  & div {
    font-size: 15px;
  }

  &:hover {
    background-color: ${props => getHoverBackground(props.operationType)};
    color: ${props => getHoverForeground(props.operationType)};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px var(--White90);
  }

  &:disabled {
    background: var(--White80);
    cursor: default;
  }
`

const SpinnerWrapper = styled.span<{ isLoading: boolean }>`
  display: block;
  position: absolute;
  top: 0;
  transform: translateY(-${props => (props.isLoading ? '0' : '50')}%);
  transition: transform ease 0.28s;

  & > div {
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

export const OperationButton = ({
  children,
  isDisabled,
  isLoading = false,
  operationType,
  ...props
}: OperationButtonProps) => (
  <Button
    {...props}
    disabled={isDisabled}
    isDisabled={isDisabled}
    isLoading={isLoading}
    operationType={operationType}
  >
    <SpinnerWrapper isLoading={isLoading}>
      <div>
        <CircularProgress color="inherit" size="20px" />
      </div>
      <div>{children}</div>
    </SpinnerWrapper>
    <div aria-hidden="true" className="hidden">
      {children}
    </div>
  </Button>
)
